<template>
  <div>
    <div class="card">
      <div class="card-header">
        <p class="text-header mb-0">Meu plano</p>
      </div>
      <LoadingContainer v-if="isLoading" />
      <div class="card-body" v-else-if="data">
        <p class="text-paragraph">
          Aqui você irá encontrar as informações referentes a assinatura do
          Psiapp.
        </p>

        <PsiappPlusCard v-if="data.subscribe_enabled" />

        <ul class="list-group list-group-flush">
          <li
            v-for="item in data.items"
            :key="item.key"
            class="list-group-item px-0"
          >
            <div
              class="d-flex justify-content-between"
              :class="{ 'mb-3': item.description }"
            >
              <span class="text-paragraph">
                {{ item.label }}
              </span>
              <span class="text-subtitle" :class="`text-${item.value.color}`">{{
                item.value.text
              }}</span>
            </div>

            <p
              v-if="item.description"
              class="text-paragraph mb-2"
              :class="`text-${item.value.color}`"
            >
              {{ item.description.text }}
            </p>

            <Button
              v-if="data.manual_payment_enabled && item.key === 'status'"
              @onClick="handleManualPay"
              :loading="manualPaying"
              fullWidth
              label="Tentar realizar pagamento novamente"
            />

            <Button
              v-if="data.has_canceled && item.key === 'status'"
              @onClick="handleReactivate"
              :loading="reactivating"
              fullWidth
              color="primary"
              :label="`Retomar assinatura do ${data.plan_title}`"
            />

            <Button
              v-if="
                data.update_credit_card_enabled && item.key === 'credit_card'
              "
              @onClick="handleCreditCards"
              fullWidth
              outline
              label="Alterar cartão de crédito"
            />
          </li>
        </ul>
      </div>
      <template v-else>
        <div
          class="d-flex justify-content-center align-items-center"
          v-if="error"
        >
          <p class="text-danger text-subtitle my-8">
            Erro ao carregar plano: {{ error?.message }}
          </p>
        </div>
      </template>
    </div>

    <div class="card" v-if="data && data.cancel_enabled">
      <div class="card-body">
        <p class="text-header">Precisa cancelar a assinatura?</p>
        <p class="text-paragraph">
          Caso você não queira mais ser um membro do Psiapp Mais e deseja perder
          todos os benefícios, você pode cancelar sua assinatura clicando
          abaixo:
        </p>

        <a href="" @click.prevent="onCancel" class="text-danger text-subtitle">
          Cancelar assinatura
        </a>
      </div>
    </div>

    <ModalComponent
      id="cancel_subs"
      title="Cancelar assinatura e perder acesso ao Psiapp?"
    >
      <template v-slot:body>
        <p class="text-paragraph">
          É uma pena saber que você gostaria de cancelar sua assinatura.
        </p>
        <p class="text-paragraph text-danger">
          Cancelando sua assinatura você não terá mais acesso ao aplicativo.
        </p>
      </template>
      <template v-slot:footer>
        <Button outline @click="onCancel" label="Não, voltar" />
        <Button
          :loading="canceling"
          color="del"
          @click="handleCancel"
          label="Sim, cancelar assinatura"
        />
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import Button from "@/components/Button.vue";
import router from "@/router";
import paths from "@/router/paths";
import { subsService } from "@/services";
import { useMainStore } from "@/store";
import { syncSessionUseCase } from "@/useCases";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { toggleModal } from "@/utils/Helper";
import {
  EOffcanvasViews,
  navigateCanvas,
  OffcanvasViewKey,
} from "@/views/OffcanvasViews/OffcanvasViewsConfig";
import PsiappPlusCard from "@/views/PsiappPlus/PsiappPlusCard.vue";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";

onMounted(() => {
  EventBus.$on(EventBusNames.REFRESH_MY_PLAN, () => refetch());
});

const { session } = useMainStore();

const subsId = computed<number>(() => {
  if (session && session.subscription) return session.subscription.id;
  return 0;
});

const route = useRoute();

const toggle = computed<boolean>(() => {
  return window.innerWidth > 964;
});

const { isLoading, refetch, data, error } = useQuery({
  queryFn: () => subsService.showSubs(subsId.value),
  queryKey: ["show_subs"],
});

const { mutateAsync: handleCancel, isPending: canceling } = useMutation({
  mutationFn: () => subsService.cancelSubs(subsId.value),
  onSuccess: async () => {
    toggleModal("cancel_subs");

    await syncSessionUseCase.execute();

    EventBus.$emit(EventBusNames.SHOW_TOAST, {
      title: "Assinatura cancelada",
      msg: `Sua assinatura ${data.value?.plan_title} foi cancelada com sucesso.`,
    });
    EventBus.$emit(EventBusNames.ON_HOLDING_UPDATE);

    refetch();
  },
  onError: (error) => {
    console.error(error);
  },
});

const { mutateAsync: handleManualPay, isPending: manualPaying } = useMutation({
  mutationFn: () => subsService.manualPaymentSubs(subsId.value),
  onSuccess: async () => {
    await syncSessionUseCase.execute();
    refetch();
    EventBus.$emit(EventBusNames.ON_HOLDING_UPDATE);
  },
  onError: (error) => {
    alert(error.message);
  },
});

const { mutateAsync: handleReactivate, isPending: reactivating } = useMutation({
  mutationFn: () => subsService.reactivateSubs(subsId.value),
  onSuccess: async () => {
    await syncSessionUseCase.execute();
    refetch();
    EventBus.$emit(EventBusNames.ON_HOLDING_UPDATE);
  },
  onError: (error) => {
    console.error(error);
  },
});

const handleCreditCards = () => {
  EventBus.$emit(EventBusNames.GET_CREDIT_CARDS);
  navigateCanvas(
    EOffcanvasViews.credit_cards as OffcanvasViewKey,
    null,
    toggle.value,
  );
};

const onCancel = () => toggleModal("cancel_subs");
</script>
